import './styles/graphic.scss';
import data from './data/data.json';
import meta from './data/meta.json';

// ============================================================================
//  "How big is $1 trillion?" — one continuous horizontal scale scroller.
//
//  A single scroll strip runs from $0 to $1 trillion so you can scroll (and
//  scroll back) seamlessly with one consistent design. The mapping from
//  dollars to pixels keeps a constant $1 = 1px FEEL everywhere:
//    • $0 → $1,000,000 is the true real scale ($1 = 1px, 1,000,000px wide).
//    • Beyond $1M each benchmark gets a fixed RUN_PX runway that also ticks at
//      $1 = 1px (so the readout climbs in hundreds, not tens of thousands).
//      Because you'd obviously never scroll $1M → $1B by hand, the runway is
//      finite (~1 min of scrolling); reaching its end auto-warps you up to the
//      next benchmark (the readout rockets to its value).
//  Ported from the design prototype into the dailygraphics rig.
// ============================================================================

// Tuning knobs.
const SCROLL_RATE = 1000; // px/sec — a realistic *human* scroll pace, used only
// for the "X more scrolling to …" estimate (auto-scroll is much faster).
// L_ORIGIN / FOCUS_PX are computed per-instance from the viewport width (see
// below) so the focus point sits at the horizontal centre on any screen size.
// FOCUS_PX is where the readout is measured AND where benchmarks come to rest;
// L_ORIGIN (== FOCUS_PX) is the x of $0, so the strip reads exactly $0 at rest
// and a benchmark reads its exact value when it lands centred (e.g. $1,000,000,
// not $999,785).
const RULE_Y = 120; // ruler line Y from top of the strip (leaves room above for the buttons)
// A labelled tick's stack (value + tick) starts 39px above the rule, so the
// "Scroll →" hint sits a line clear of that rather than on top of the numbers.
const HINT_Y = RULE_Y - 62;
const REAL_MAX = 1000000; // $1M — end of the true real-scale region
const RUN_PX = 200000; // px of $1=1px forward runway after each simulated benchmark
const APPROACH = 72000; // px of $1=1px runway BEFORE each simulated benchmark — a
// generous "amount on each side" of every big number (a $1=1px mini real-time
// timeline, ~1 min of scrolling at SCROLL_RATE) before the leg boundary drops
// you back to the previous benchmark. Must exceed the landing/focus point so
// benchmarks still land centred on warp. The "Warp to →" button appears the
// moment you scroll back and jumps you forward to the benchmark again.
const RUNWAY_END = 2; // auto-warp once within this many px of a leg's end
// Warp durations spread by the LOG of each benchmark gap (linear in orders of
// magnitude), so the growing distances read as a clear, watchable progression
// instead of several instant jumps and one huge one. The smallest gap maps to
// WARP_SHORT_MS, the biggest (non-final) gap to WARP_LONG_MS; the final warp to
// the top benchmark is excluded and uses the custom WARP_FINALE_MS.
const WARP_SHORT_MS = 1000; // shortest warp (smallest benchmark gap)
const WARP_LONG_MS = 8000; // longest non-final warp (biggest gap before the finale)
const WARP_FINALE_MS = 15000; // custom duration for the final warp to the top benchmark
const C = {
  screen: '#ffffff',
  ink: '#262626', // CNN text/labels
  muted: '#8e8e8e', // CNN source/note grey
  faint: '#A6A6A6', // CNN axis-label grey
  faint2: '#A6A6A6',
  rule: '#B1B1B1', // CNN gridline grey
  tick: '#A6A6A6',
  tickMinor: '#D9D9D9', // $100 ticks — lighter than the labelled ones
  stem: '#B1B1B1',
  accent: '#53A796', // CNN teal
  chipBg: '#eaf4f0',
  chipBorder: '#cfe6de',
  dark: '#262626',
  light: '#f5f5f5',
};

// CNN default text stack (matches the rig's $font-family-sans-serif).
const FONT = '"CNN", "CNN Sans Display", "CNN Sans", "Helvetica Neue", Helvetica, Utkal, Arial, sans-serif';

const easeInOut = t => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);

function fmt(n) {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}
function compact(n) {
  return `$${new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(n)}`;
}
// Real dollars get the full figure; big numbers get a compact one.
const money = v => (v < REAL_MAX ? fmt(v) : compact(v));

// Humanize a duration (seconds) down to the seconds so it visibly ticks as you
// scroll (e.g. "4m 32s", "9d 15h 55m 33s"). Anything a year or longer stays
// coarse ("26.4 years") since second-level detail there is meaningless.
function humanize(sec) {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s} sec.`;
  const days = Math.floor(s / 86400);
  if (days >= 365) return `${+(days / 365).toFixed(1)} years`;
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (days > 0) return `${days}d ${h}h ${m}m ${ss}s`;
  if (h > 0) return `${h}h ${m}m ${ss}s`;
  return `${m}m ${ss}s`;
}

// Fully spelled-out duration for the milestone sentences, e.g.
// "16 minutes and 40 seconds" or "11 days, 13 hours, 46 minutes and 40 seconds".
function humanizeWords(sec) {
  const unit = (n, name) => `${n.toLocaleString('en-US')} ${name}${n === 1 ? '' : 's'}`;
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return unit(s, 'second');
  const days = Math.floor(s / 86400);
  if (days >= 365) return unit(Math.round(days / 365), 'year');
  const parts = [];
  if (days > 0) parts.push(unit(days, 'day'));
  const h = Math.floor((s % 86400) / 3600);
  if (h > 0) parts.push(unit(h, 'hour'));
  const m = Math.floor((s % 3600) / 60);
  if (m > 0) parts.push(unit(m, 'minute'));
  const ss = s % 60;
  if (ss > 0) parts.push(unit(ss, 'second'));
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

// Brand/proper-noun first words that keep their capital mid-sentence even
// though they aren't acronyms or internally capitalised. Add new ones here.
const PROPER_STARTS = new Set(['Big', 'Gulfstream']);

// Lowercase the first letter of a benchmark label so it reads as part of a
// sentence ("… to scroll to average price of a new car"), UNLESS the leading
// word is a proper noun: an acronym (NASA, US), internally capitalised (SoFi),
// a listed brand (Big Mac, Gulfstream), or the label starts with a non-letter ($1M).
function sentenceCase(label) {
  const str = String(label);
  if (!/^[A-Za-z]/.test(str)) return str;
  const first = str.split(' ')[0];
  const isAcronym = first.length > 1 && first === first.toUpperCase();
  const hasInnerCap = /[A-Z]/.test(first.slice(1));
  if (isAcronym || hasInnerCap || PROPER_STARTS.has(first)) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
}

// Glue the last two words + trailing emoji together with non-breaking spaces so
// the line never wraps to leave an orphan (just an emoji, or a single word) on
// its own — the tail always carries at least two words and the emoji.
function noOrphan(label, emoji) {
  const words = String(label).split(' ').filter(Boolean);
  const tailN = Math.min(2, words.length);
  const head = words.slice(0, words.length - tailN).join(' ');
  const tail = words.slice(words.length - tailN).join('\u00A0');
  const joined = head ? `${head} ${tail}` : tail;
  return emoji ? `${joined}\u00A0${emoji}` : joined;
}

// Small DOM helper: create an element, apply inline styles + props/children.
function el(tag, styles, props) {
  const node = document.createElement(tag);
  if (styles) Object.assign(node.style, styles);
  if (props) {
    Object.keys(props).forEach((k) => {
      if (k === 'text') node.textContent = props[k];
      else if (k === 'html') node.innerHTML = props[k];
      else if (k === 'children') props[k].forEach(c => c && node.appendChild(c));
      else if (k.startsWith('on') && typeof props[k] === 'function') {
        node.addEventListener(k.slice(2).toLowerCase(), props[k]);
      } else node.setAttribute(k, props[k]);
    });
  }
  return node;
}

let activeCleanup = null;

export default function (props, container, isMobile) {
  if (activeCleanup) activeCleanup();

  // --- Data: flat, ascending list from the research spreadsheet. -----------
  const toNumber = c => Number(String(c).replace(/[^0-9.]/g, ''));
  const shortLabel = c => String(c).split(/[,(]/)[0].trim();
  const rows = (Array.isArray(data) ? data : [])
    .map(d => ({
      emoji: d.emoji,
      label: shortLabel(d.comparison),
      value: toNumber(d['approx-cost']),
    }))
    .filter(d => Number.isFinite(d.value) && d.value > 0);

  // $0 (and the Big Mac / gas markers) sit at the far left via a small L_ORIGIN,
  // while the focus point — where the readout is measured AND where benchmarks
  // come to rest — is the horizontal centre of the embed. Because FOCUS_PX >
  // L_ORIGIN, the focus already sits ~$200 into the strip at rest; we hide that
  // by DISPLAYING $0 until the reader first scrolls (see render's `live`), then
  // the number jumps to the true value at the focus and climbs from there.
  // Reading and landing both use FOCUS_PX, so benchmarks still land dead-centre
  // with their exact value (e.g. $1,000,000, not $999,785).
  const measuredW = (() => {
    try { return container.node().clientWidth || 0; } catch (e) { return 0; }
  })();
  const rawW = measuredW || (isMobile ? (window.innerWidth || 360) : 780);
  const VIEW_W = rawW; // fill the embed on both mobile and desktop (no phone-width cap)
  const L_ORIGIN = 24; // x of $0 — flush-left under the header padding
  const FOCUS_PX = Math.max(140, Math.round(VIEW_W / 2)); // reading + landing point (centre)

  // Benchmarks that define the simulated legs: $1M through $1T.
  const JRN = rows.filter(d => d.value >= REAL_MAX && d.value <= 1e12);
  const LEGS = Math.max(0, JRN.length - 1);
  const J1M_X = L_ORIGIN + REAL_MAX; // x of the $1M seam
  // Each simulated benchmark owns an APPROACH zone (so it can land centred when
  // warped to, like the real scale) followed by a RUN_PX forward runway, so the
  // markers sit SPAN px apart. Everything is still $1 = 1px within a leg.
  const SPAN = RUN_PX + APPROACH;
  const markerX = i => J1M_X + i * SPAN; // x of benchmark JRN[i]
  const STRIP_W = markerX(LEGS) + 300;
  const MAX_VALUE = JRN.length ? JRN[JRN.length - 1].value : REAL_MAX;
  // Which simulated leg an x falls in (leg i spans [markerX(i)-APPROACH, +RUN_PX)).
  const simLeg = x => Math.max(0, Math.min(LEGS, Math.floor((x - J1M_X + APPROACH) / SPAN)));

  // One timing curve for EVERY auto transition — real-scale auto-scroll and
  // simulated warp alike — so the two scales read as a single system. Duration
  // is spread by the LOG of each benchmark gap across the whole journey ($11 →
  // top), mapped onto [WARP_SHORT_MS, WARP_LONG_MS]; time therefore rises
  // smoothly from the smallest step to the largest across the $1M seam. The
  // final step to the top benchmark is excluded and uses the custom WARP_FINALE_MS.
  const BENCH = rows.filter(d => d.value <= MAX_VALUE);
  // Calibrate the curve over only the gaps that are actually auto-scrolled or
  // warped — i.e. from the first "Next benchmark" step (university tuition → new
  // car) onward — so those durations spread across the full [SHORT, LONG] range
  // for a clear progression, instead of being squashed by the tiny intro gaps
  // ($11 Big Mac → $60 gas, etc.) that the reader only ever passes by hand.
  const autoStartRow = BENCH.find(d => d.emoji === '🎓' || /tuition/i.test(d.label));
  const autoStartValue = autoStartRow ? autoStartRow.value : (BENCH[0] ? BENCH[0].value : 0);
  let minLog = Infinity;
  let maxLog = -Infinity;
  for (let i = 1; i < BENCH.length - 1; i += 1) { // exclude the finale gap (→ top)
    if (BENCH[i - 1].value < autoStartValue) continue; // skip pre-button intro gaps
    const lg = Math.log(Math.max(1, BENCH[i].value - BENCH[i - 1].value));
    if (lg < minLog) minLog = lg;
    if (lg > maxLog) maxLog = lg;
  }
  const warpDur = (dDollars) => {
    if (!(maxLog > minLog)) return WARP_SHORT_MS;
    const t = (Math.log(Math.max(1, Math.abs(dDollars))) - minLog) / (maxLog - minLog);
    const c = Math.max(0, Math.min(1, t));
    return WARP_SHORT_MS + c * (WARP_LONG_MS - WARP_SHORT_MS);
  };

  // All markers, ascending; the real region owns the $1M marker so we drop
  // the duplicate $1M from the journey list.
  const MARKERS = rows
    .filter(d => d.value <= MAX_VALUE)
    .filter((d, i, arr) => !(d.value === REAL_MAX && arr.findIndex(x => x.value === REAL_MAX) !== i));

  // dollars → x and x → dollars. Real scale is one linear ramp; each simulated
  // leg is its own $1 = 1px runway around its benchmark. Leg boundaries are
  // intentionally discontinuous — that jump is the "warp" to the next benchmark.
  function xOf(v) {
    if (v <= REAL_MAX) return L_ORIGIN + v;
    let i = 0;
    for (let k = 0; k <= LEGS; k += 1) { if (JRN[k].value <= v) i = k; else break; }
    const off = Math.max(-APPROACH, Math.min(RUN_PX, v - JRN[i].value));
    return markerX(i) + off;
  }
  function dollarsOf(x) {
    const xr = x - L_ORIGIN;
    if (xr <= REAL_MAX) return Math.max(0, xr);
    const i = simLeg(x);
    return JRN[i].value + (x - markerX(i)); // $1 = 1px within the leg
  }

  // The "Next benchmark" button appears once you're past the gas marker, so it's
  // available at every step from credit-card debt onward (its first target). Big
  // Mac and gas stay button-free — they're the manual $1 = 1px intro.
  const tuitionRow = MARKERS.find(m => m.emoji === '🎓') || MARKERS.find(m => /tuition/i.test(m.label));
  const carRow = MARKERS.find(m => m.emoji === '🚗') || MARKERS.find(m => /new car/i.test(m.label));
  const gasRow = MARKERS.find(m => m.emoji === '⛽') || MARKERS.find(m => /gas/i.test(m.label));
  const NEXT_MIN = (gasRow && xOf(gasRow.value))
    || (tuitionRow && xOf(tuitionRow.value))
    || (carRow && xOf(carRow.value)) || 3000;

  // $1M and $1B get the same celebratory milestone treatment as the $1T finale
  // (a 🎉 on the readout + "this would have taken X of real scrolling"), but they
  // aren't terminal — the reader keeps scrolling/warping onward. $1T itself is
  // the true end and is handled by endBox / state.ended.
  const MILE_VALUES = [REAL_MAX, 1e9];

  // Credit-card debt and tuition are only reached by manual scrolling and are
  // easy to blow past, so we damp the wheel/trackpad speed as each approaches
  // (see bindWheel) — a gentle "slow down" rather than a snap, so there's no jump.
  const ccRow = MARKERS.find(m => m.emoji === '💳') || MARKERS.find(m => /credit/i.test(m.label));
  const SLOW_VALUES = [ccRow, tuitionRow].filter(Boolean).map(m => m.value);
  const SLOW_FACTOR = 0.15; // scroll at 15% speed through each benchmark's approach

  const root = container.node();

  // Stem elements are collected here so fitStems() can scale them to whatever
  // vertical space is left under the timeline (the dek's length changes the
  // header height, so a fixed stem can't reliably keep markers on screen).
  const stems = [];

  // The recycled pool of $100 tick labels, and the x of every labelled major
  // tick — a minor label there would sit under the major's own (see
  // positionTickLabels).
  const tickLabels = [];
  const majorX = new Set();
  // The simulated benchmarks land on the tick lattice too, but their own marker
  // below the rule already states the value, so they don't take a tick label.
  JRN.forEach((_, i) => majorX.add(markerX(i)));

  // ---- Outer frame: a phone-width column that fills the embed. ----------
  const TOTAL_H = 600;
  const wrap = el('div', {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    height: `${TOTAL_H}px`,
    margin: '0 auto',
    background: C.screen,
    borderRadius: '18px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    color: C.ink,
    fontFamily: FONT,
    fontVariantLigatures: 'none',
  });
  wrap.className = 'trillion-scroller';
  root.appendChild(wrap);

  // ------------------------------------------------------------------ state
  const state = {
    sl: 0,
    autoScrolling: false,
    ended: false,
    benchActive: false,
    remaining: '',
    label: '',
    warping: false,
    warpNum: null,
    warpTarget: null,
  };

  let scrollEl = null;
  let raf = null;
  let wheelH = null;
  // track.baseHeaderH caches the header height in the normal state, used to keep
  // the timeline at a fixed vertical position when the taller end blurb appears;
  // track.stripEl references the inner strip so we can shift it up in that state.
  const track = {
    sl: 0, t: 0, cooldown: 0, baseHeaderH: 0, stripEl: null, wheelUntil: 0,
  };

  const cwOf = () => (scrollEl && scrollEl.clientWidth) || 360;

  function cleanup() {
    if (raf) cancelAnimationFrame(raf);
    if (scrollEl && wheelH) scrollEl.removeEventListener('wheel', wheelH);
    raf = null;
  }
  activeCleanup = cleanup;

  // True while scrollLeft is in the lead-in → just-past-centre window of one of
  // the easy-to-miss manual benchmarks, so the scroll can be slowed there.
  function inSlowZone(sl) {
    const cw = cwOf();
    return SLOW_VALUES.some((v) => {
      const enter = xOf(v) - cw - 400; // begins just before it scrolls into view
      const settle = xOf(v) - FOCUS_PX + 120; // ends just past centre
      return sl >= enter && sl <= settle;
    });
  }

  function bindWheel(node) {
    const h = (e) => {
      if (state.autoScrolling) return;
      let d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (d !== 0) {
        e.preventDefault();
        // Snap-stop fights the mouse wheel (it keeps re-centring the sentinel and
        // you get stuck). Disable snapping while actively wheeling; touch scrolling
        // fires no wheel events, so it keeps its snap. See render().
        track.wheelUntil = Date.now() + 250;
        node.style.scrollSnapType = 'none';
        if (inSlowZone(node.scrollLeft)) d *= SLOW_FACTOR;
        node.scrollLeft += d;
      }
    };
    node.addEventListener('wheel', h, { passive: false });
    wheelH = h;
  }

  // =========================================================== HEADER =======
  const header = el('div', { padding: '24px 0 14px', flex: '0 0 auto', zIndex: '10' });
  wrap.appendChild(header);

  const title = el('div', {
    fontSize: '18px',
    fontWeight: '700',
    color: C.ink,
    letterSpacing: '-0.02em',
    lineHeight: '1.15',
    marginBottom: '6px',
  }, { text: (meta && meta.headline) || 'How big is $1 trillion?' });
  header.appendChild(title);

  // Dek (subhed) straight from the Google Sheet's copy tab.
  if (meta && meta.subhed) {
    const dek = el('div', {
      fontSize: '15px',
      fontWeight: '300', // CNN dek: Light
      color: C.ink,
      lineHeight: '1.45',
      marginBottom: '12px',
    }, { text: meta.subhed });
    header.appendChild(dek);
  }

  // Rate caption — plain grey text that sits UNDER the readout/hero (appended
  // after readoutBox below), e.g. "$1,200 = 1 sec of scrolling".
  const chip = el('div', {
    marginTop: '7px',
    maxWidth: '100%',
    fontSize: '12px',
    fontWeight: '500',
    letterSpacing: '0',
    color: C.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });

  const readoutBox = el('div', {});
  const readout = el('div', {
    marginTop: '12px',
    height: '40px',
    display: 'flex',
    alignItems: 'flex-end',
    fontSize: '38px',
    fontWeight: '800',
    color: C.ink,
    letterSpacing: '-0.02em',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: '1',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  });
  readoutBox.appendChild(readout);
  // Outrageous "time to next benchmark" (simulated only). A fixed height keeps
  // the header the same size whether or not the line is showing, so the
  // real→simulated transition doesn't bump the timeline.
  const heroSub = el('div', {
    marginTop: '8px',
    minHeight: '18px', // at least one line; grows to two when a name wraps
    fontSize: '13px',
    fontWeight: '500', // reverted to original weight; only the color change kept
    color: C.ink,
    lineHeight: '18px',
    whiteSpace: 'normal',
    overflow: 'hidden',
  });
  // The countdown time is its own span whose width is locked to the widest value
  // seen during the current benchmark's approach, so the ticking number never
  // reflows the benchmark text (which would make words "whip" between lines).
  const heroTime = el('span', {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    textAlign: 'left',
    fontVariantNumeric: 'tabular-nums',
    color: C.accent,
    fontWeight: '700',
  });
  const heroTail = el('span', {});
  heroSub.appendChild(heroTime);
  heroSub.appendChild(heroTail);
  const heroTrack = { key: '', w: 0 };
  // Milestone blurb — shown ABOVE the hero line when centred on $1M/$1B, echoing
  // the $1T end scene ("This would have taken X of real scrolling."). The hero
  // line ("X to scroll to <next>") stays visible beneath it.
  const mileSub = el('div', {
    marginTop: '8px',
    fontSize: '13px',
    fontWeight: '300', // CNN annotation: Light, near-black (teal value is the highlight)
    color: C.ink,
    lineHeight: '1.45',
    display: 'none',
  });
  readoutBox.appendChild(mileSub);
  readoutBox.appendChild(heroSub);
  header.appendChild(readoutBox);
  header.appendChild(chip);

  // ---- End state ----
  // Total real-scrolling time at the same human pace used for the estimates:
  // $1 → $1T at $1 = 1px, and the extra to reach $40T. Spelled out to match the
  // $1M/$1B milestone sentences.
  const timeToT = humanizeWords(1e12 / SCROLL_RATE);
  const timeTo40T = humanizeWords((40e12 - 1e12) / SCROLL_RATE);
  const endBox = el('div', { marginTop: '12px', animation: 'trFadeUp 0.4s ease-out' });
  endBox.appendChild(
    el('div', {
      fontSize: '30px', fontWeight: '800', color: C.ink, letterSpacing: '-0.02em',
    }, { text: '$1 trillion 🎉' }),
  );
  const tealVal = t => `<span style="color:${C.accent};font-weight:700">${t}</span>`;
  endBox.appendChild(
    el('div', {
      fontSize: '14px', color: C.ink, fontWeight: '400', marginTop: '6px', lineHeight: '1.45',
    }, {
      html: `This would have taken ${tealVal(timeToT)} of real scrolling. `
        + `$40 trillion would take another ${tealVal(timeTo40T)}.`,
    }),
  );
  // The scroll-rate caption moves under the end blurb once you've reached $1T.
  endBox.appendChild(
    el('div', {
      marginTop: '10px', fontSize: '12px', fontWeight: '500', color: C.muted,
    }, { text: `$${SCROLL_RATE.toLocaleString('en-US')} = 1 sec of scrolling` }),
  );
  endBox.appendChild(
    el('button', {
      marginTop: '14px',
      background: C.dark,
      color: '#fff',
      border: 'none',
      borderRadius: '14px',
      padding: '9px 14px',
      fontFamily: 'inherit',
      fontSize: '14px',
      fontWeight: '700',
      letterSpacing: '-0.01em',
      boxShadow: '0 0 14px rgba(0,0,0,0.15)',
      cursor: 'pointer',
    }, { text: '↺ Back to $0', onClick: () => restartAll() }),
  );
  header.appendChild(endBox);

  // ============================================================ STAGE =======
  const stage = el('div', { flex: '1 1 auto', position: 'relative', overflow: 'hidden' });
  wrap.appendChild(stage);
  // Source / credit live in the page footer (public/index.html), populated from
  // the sheet's copy tab (meta.json) by the rig.

  // ---- Floating controls ----
  const hint = el('div', {
    position: 'absolute',
    top: `${HINT_Y}px`,
    right: '16px',
    zIndex: '15',
    pointerEvents: 'none',
    textAlign: 'right',
    transition: 'opacity 0.25s ease',
  });
  hint.appendChild(
    el('div', { fontSize: '15px', fontWeight: '700', color: C.accent }, {
      html: '<span style="display:inline-block;animation:trNudge 1.4s ease-in-out infinite;">Scroll →</span>',
    }),
  );

  // Next-benchmark button — green, just above the timeline, right.
  const nextBtn = el('button', {
    position: 'absolute',
    top: `${RULE_Y - 12}px`,
    right: '18px',
    transform: 'translateY(-100%)',
    background: C.accent,
    color: '#fff',
    border: 'none',
    borderRadius: '14px',
    padding: '9px 14px',
    fontFamily: 'inherit',
    textAlign: 'right',
    boxShadow: '0 0 14px rgba(0,0,0,0.15)',
    cursor: 'pointer',
    zIndex: '20',
    maxWidth: '170px',
    animation: 'trFadeIn 0.3s ease-out',
  }, {
    onClick: () => {
      // Real scale: slow auto-scroll to the next benchmark. Simulated scale:
      // this slot becomes a warp button that jumps to the next benchmark.
      if (dollarsOf(state.sl + FOCUS_PX) >= REAL_MAX) warp();
      else goNext();
    },
  });
  const nextTitle = el('div', {
    fontSize: '14px', fontWeight: '700', letterSpacing: '-0.01em',
  }, { text: 'Skip ahead →' });
  nextBtn.appendChild(nextTitle);
  const nextSub = el('div', {
    fontSize: '11px', fontWeight: '500', opacity: '0.78', marginTop: '2px', lineHeight: '1.2', maxWidth: '146px',
  });
  nextBtn.appendChild(nextSub);

  // Auto-scroll countdown — styled like the next button, same spot.
  const chipBtn = el('div', {
    position: 'absolute',
    top: `${RULE_Y - 12}px`,
    right: '18px',
    transform: 'translateY(-100%)',
    background: C.accent,
    color: '#fff',
    border: 'none',
    borderRadius: '14px',
    padding: '9px 14px',
    fontFamily: 'inherit',
    textAlign: 'right',
    boxShadow: '0 0 14px rgba(0,0,0,0.15)',
    cursor: 'pointer',
    zIndex: '20',
    maxWidth: '170px',
    animation: 'trFadeIn 0.3s ease-out',
  }, { onClick: () => cancelAuto() });
  const chipNum = el('div', {
    fontSize: '14px', fontWeight: '700', letterSpacing: '-0.01em',
  });
  const chipLabel = el('div', {
    fontSize: '11px', fontWeight: '500', opacity: '0.78', marginTop: '2px', lineHeight: '1.2', maxWidth: '146px',
  });
  chipBtn.appendChild(chipNum);
  chipBtn.appendChild(chipLabel);

  // -------------------------------------------------------- strip build -----
  function markerNode(m, idx) {
    const accent = m.value === REAL_MAX || m.value === MAX_VALUE;
    // Uniform stem length for every benchmark, except Big Mac (🍔) and gas (⛽),
    // which sit ~49px apart and must be staggered so their labels don't collide.
    // Base/ideal stem lengths; fitStems() scales them down to fit the stage.
    const UNIFORM_STEM = 70;
    let stemH = accent ? 128 : UNIFORM_STEM;
    if (m.emoji === '🍔') stemH = 52;
    else if (m.emoji === '⛽') stemH = 120;
    const wrapEl = el('div', {
      position: 'absolute',
      left: `${xOf(m.value)}px`,
      top: `${RULE_Y - 5}px`,
      transform: 'translateX(-50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      zIndex: String(accent ? 8 : 5),
    });
    const dot = el('div', {
      width: '11px',
      height: '11px',
      borderRadius: '50%',
      background: C.accent,
      border: `2px solid ${C.screen}`,
      boxShadow: `0 0 0 1px ${C.accent}`,
    });
    wrapEl.appendChild(dot);
    const stemEl = el('div', {
      width: '2px', height: `${stemH}px`, background: accent ? C.accent : C.stem,
    });
    stems.push({ el: stemEl, base: stemH });
    wrapEl.appendChild(stemEl);
    wrapEl.appendChild(
      el('div', { fontSize: '26px', lineHeight: '1', marginTop: '6px' }, { text: m.emoji }),
    );
    wrapEl.appendChild(
      el('div', {
        fontSize: accent ? '22px' : '17px',
        fontWeight: '800',
        color: accent ? C.accent : C.ink,
        letterSpacing: '-0.01em',
        fontVariantNumeric: 'tabular-nums',
        marginTop: '4px',
        whiteSpace: 'nowrap',
      }, { text: money(m.value) }),
    );
    const wrapText = m.emoji === '🍔' || m.emoji === '⛽';
    wrapEl.appendChild(
      el('div', {
        fontSize: '12px',
        color: C.muted,
        fontWeight: '500',
        whiteSpace: wrapText ? 'normal' : 'nowrap',
        width: wrapText ? '64px' : 'auto',
        textAlign: 'center',
        lineHeight: wrapText ? '1.15' : '1',
        marginTop: '2px',
      }, { text: m.label }),
    );
    wrapEl.dot = dot;
    return wrapEl;
  }

  function buildStrip() {
    const scroll = el('div', {
      position: 'absolute',
      inset: '0',
      overflowX: 'auto',
      overflowY: 'hidden',
      WebkitOverflowScrolling: 'touch',
      // Native snap so a fast touch fling can't blow past the easy-to-miss manual
      // benchmarks (credit-card debt, tuition). Proximity keeps everywhere else
      // free; the per-marker sentinels below use scroll-snap-stop: always so each
      // one halts a fling. Toggled off during warp/auto-scroll in render().
      scrollSnapType: 'x proximity',
    });
    scroll.className = 'tr-scroll-area';
    const strip = el('div', { position: 'relative', height: '100%', width: `${STRIP_W}px` });
    track.stripEl = strip;
    strip.appendChild(el('div', {
      position: 'absolute', left: `${L_ORIGIN}px`, right: '0', top: `${RULE_Y}px`, height: '3px', background: C.accent,
    }));

    // Minor $100 ticks. At $1 = 1px that's one every 100px over a million-pixel
    // real scale, so each region gets ONE element painted with a repeating
    // gradient — 10,000 nodes would be ruinous. `phase` shifts the pattern so a
    // line always lands on a round-hundred dollar value. Drawn before the
    // labelled ticks so those sit on top.
    const minorTicks = (left, width, phase) => el('div', {
      position: 'absolute',
      left: `${left}px`,
      top: `${RULE_Y - 8}px`,
      width: `${width}px`,
      height: '8px', // sits flush on top of the rule
      backgroundImage: `repeating-linear-gradient(to right, ${C.tickMinor} 0px, ${C.tickMinor} 2px, transparent 2px, transparent 100px)`,
      backgroundPosition: `${phase}px 0`,
    });
    // Real scale: starts at the x of $0, so it's in phase already.
    strip.appendChild(minorTicks(L_ORIGIN, REAL_MAX, 0));
    // Each simulated leg is $1 = 1px too, so it carries the same texture, keyed
    // to its own benchmark: APPROACH px of back-scroll runway before the marker
    // and RUN_PX after. Leg 0 gets nothing before its marker — the real scale
    // runs continuously up to the $1M seam and already covers it. The right edge
    // is capped at the strip so the last leg can't add scrollable blank space.
    for (let i = 0; i <= LEGS; i += 1) {
      const left = i === 0 ? markerX(0) : markerX(i) - APPROACH;
      const right = Math.min(markerX(i) + RUN_PX, STRIP_W);
      strip.appendChild(minorTicks(left, right - left, (markerX(i) - left) % 100));
    }

    // $0 needs no marker of its own: it's the first line of the minor-tick
    // gradient, and the label pool gives it a "$0" like any other $100 tick.

    // Real-scale ticks: every $10k below $100k, every $50k up to $1M.
    const tickVals = [];
    for (let v = 10000; v < 100000; v += 10000) tickVals.push(v);
    for (let v = 100000; v < 1000000; v += 50000) tickVals.push(v);
    tickVals.forEach((v) => {
      const t = el('div', {
        position: 'absolute',
        left: `${L_ORIGIN + v}px`,
        top: `${RULE_Y - 39}px`,
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      });
      t.appendChild(el('div', {
        fontSize: '10px', color: C.faint, fontWeight: '600', whiteSpace: 'nowrap',
      }, { text: `$${v / 1000}k` }));
      t.appendChild(el('div', {
        width: '2px', height: '24px', background: C.tick, marginTop: '3px',
      }));
      strip.appendChild(t);
      majorX.add(L_ORIGIN + v);
    });

    // Simulated legs: the same $50k ruler ticks as the real scale (identical px
    // spacing, since each leg is still $1 = 1px). Label them where the compact
    // value is actually distinguishable from the benchmark (smaller legs); on
    // huge legs the labels would all read the same, so those ticks stay bare.
    for (let i = 0; i < LEGS; i += 1) {
      const base = JRN[i].value;
      const baseLabel = compact(base);
      for (let off = 50000; off < RUN_PX; off += 50000) {
        const lab = compact(base + off);
        const t = el('div', {
          position: 'absolute',
          left: `${markerX(i) + off}px`,
          top: `${RULE_Y - 39}px`,
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        });
        t.appendChild(el('div', {
          fontSize: '10px', color: C.faint, fontWeight: '600', whiteSpace: 'nowrap', height: '12px',
        }, { text: lab !== baseLabel ? lab : '' }));
        t.appendChild(el('div', {
          width: '2px', height: '24px', background: C.tick, marginTop: '3px',
        }));
        strip.appendChild(t);
        // Bare ticks (no distinguishable compact value) leave room for a $100
        // label, so only the labelled ones block one.
        if (lab !== baseLabel) majorX.add(markerX(i) + off);
      }
    }

    // Labels for the $100 ticks, drawn from a recycled pool — see
    // positionTickLabels for why they can't be static. Sized to the widest
    // viewport we could be showing, plus a couple of ticks of slack at each edge
    // so a label is already in place before its tick scrolls into view.
    const poolSize = Math.ceil(VIEW_W / 100) + 4;
    for (let k = 0; k < poolSize; k += 1) {
      const lab = el('div', {
        position: 'absolute',
        top: `${RULE_Y - 23}px`, // 3px above the minor ticks
        transform: 'translateX(-50%)',
        fontSize: '10px',
        fontWeight: '500', // lighter than the labelled majors, which stay at 600
        color: C.faint,
        whiteSpace: 'nowrap',
        lineHeight: '12px',
        display: 'none',
      });
      tickLabels.push(lab);
      strip.appendChild(lab);
    }

    MARKERS.forEach((m, i) => {
      strip.appendChild(markerNode(m, i));
    });

    // Snap sentinels: a zero-width target centred on each easy-to-miss benchmark.
    // scroll-snap-stop: always forces a touch fling to halt here so it can't be
    // missed; scroll-snap-align: center lands it at the focus point.
    SLOW_VALUES.forEach((v) => {
      strip.appendChild(el('div', {
        position: 'absolute',
        left: `${xOf(v)}px`,
        top: `${RULE_Y}px`,
        width: '1px',
        height: '1px',
        scrollSnapAlign: 'center',
        scrollSnapStop: 'always',
      }));
    });

    scroll.appendChild(strip);
    scroll.addEventListener('scroll', onScroll);
    return scroll;
  }

  // Pin a floating control to `base` (a y measured from the top of the stage),
  // clamped so it can never be pushed off the top. The buttons are anchored to
  // the timeline and grow UPWARD (translateY(-100%)), so their real top edge is
  // base - height; on narrow screens, where the wrapped hero/milestone lines
  // make the header tall and the wrapped benchmark name makes the button tall,
  // an unclamped base went negative and the stage's overflow:hidden swallowed
  // the control entirely. Overlapping the header slightly beats vanishing.
  function pinAbove(node, base, growsUp) {
    if (node.style.display === 'none') return;
    const h = growsUp ? node.offsetHeight : 0;
    node.style.top = `${Math.max(h + 2, base)}px`;
  }

  // What a $100 tick reads, or null if it shouldn't be labelled at all. The ticks
  // themselves stay 100px apart everywhere; only the values thin out — every $100
  // in the real scale, every $500 past $1M, every $5,000 past $1B. Since a dollar
  // interval is also a pixel interval here, thinning both keeps the long figures
  // from colliding and stops their trailing digits churning too fast to read.
  // The interval keys off the leg's benchmark rather than the value under the
  // cursor, so it can't switch partway through a leg — the $1B leg's back-scroll
  // runway sits below a billion.
  function tickText(x, d) {
    if (x <= J1M_X) return fmt(d);
    const step = JRN[simLeg(x)].value >= 1e9 ? 5000 : 500;
    return d % step === 0 ? fmt(d) : null;
  }

  // Point the label pool at the $100 ticks currently in view. Labelling the whole
  // strip would take ~34,000 nodes, but at $1 = 1px only a few hundred dollars of
  // scale are on screen at once, so a pool of a dozen is re-pointed each frame
  // instead. The labels live in the strip, not the stage, so they stay glued to
  // their ticks even when a scroll event lags the visual scroll.
  // Every tick on the strip — real scale and simulated legs alike — sits at
  // x ≡ L_ORIGIN (mod 100), because the leg offsets are all whole hundreds. So
  // one walk of that lattice covers both scales.
  function positionTickLabels() {
    if (!tickLabels.length) return;
    const sl = scrollEl ? scrollEl.scrollLeft : state.sl;
    const first = L_ORIGIN
      + Math.ceil((Math.max(L_ORIGIN, sl - 200) - L_ORIGIN) / 100) * 100;
    const last = sl + cwOf() + 200;
    tickLabels.forEach((node, k) => {
      const x = first + k * 100;
      const text = (x > last || x > STRIP_W || majorX.has(x))
        ? null
        : tickText(x, dollarsOf(x));
      if (!text) {
        node.style.display = 'none';
        return;
      }
      node.style.display = 'block';
      node.style.left = `${x}px`;
      node.textContent = text;
    });
  }

  // --------------------------------------------------------- rendering ------
  function nextMarker() {
    // The next benchmark is the first one whose marker is past the focus point,
    // so the one we're currently centred on isn't counted as "next".
    const edge = state.sl + FOCUS_PX;
    return MARKERS.find(m => xOf(m.value) > edge);
  }

  function render() {
    positionTickLabels();
    const rawDollars = dollarsOf(state.sl + FOCUS_PX);
    // Before the reader scrolls, park the display at $0 (the strip is at rest with
    // $0/the Big Mac at the left). The moment they move, show the true value at
    // the focus point — the readout jumps up and then climbs $1 = 1px from there.
    const live = state.sl > 0;
    const dollars = state.warpNum != null ? state.warpNum : (live ? rawDollars : 0);
    const inReal = rawDollars < REAL_MAX;
    chip.textContent = `$${SCROLL_RATE.toLocaleString('en-US')} = 1 sec of scrolling`;

    // On a milestone ($1M/$1B) once we've come to rest centred on it (not mid-warp
    // or auto-scroll). Uses a small dollar window (== px, since $1 = 1px near each).
    const onMile = (!state.warping && !state.autoScrolling && live)
      ? MILE_VALUES.find(v => Math.abs(dollars - v) < 40)
      : undefined;

    readoutBox.style.display = state.ended ? 'none' : 'block';
    endBox.style.display = state.ended ? 'block' : 'none';
    chip.style.display = state.ended ? 'none' : 'block';
    // Anything that makes the header taller — the $1M/$1B milestone blurb, a
    // hero line that wraps to two lines, the end blurb — shrinks the stage and
    // would push the markers hanging below the timeline off the bottom (the $1M
    // and $1T accent markers hang lowest, so they clip first). Shift the strip up
    // by exactly that extra height so the timeline, and the deepest marker with
    // it, stay put.
    if (!track.baseHeaderH) track.baseHeaderH = header.offsetHeight;
    const headExtra = Math.max(0, header.offsetHeight - track.baseHeaderH);
    if (track.stripEl) {
      track.stripEl.style.transform = headExtra ? `translateY(${-headExtra}px)` : 'none';
    }
    // The floating controls live in the stage, not the strip, so pin them to the
    // shifted timeline or they'd end up sitting on top of it. The two buttons are
    // pinned further down, once their visibility is known — pinAbove has to
    // measure them, which only works while they're displayed.
    pinAbove(hint, HINT_Y - headExtra, false);
    const btnBase = RULE_Y - 12 - headExtra;

    // Turn snapping off while we programmatically warp/auto-scroll so it can't
    // yank the animation to a snap point; restore it for free manual scrolling.
    if (scrollEl) {
      const wheeling = Date.now() < track.wheelUntil;
      scrollEl.style.scrollSnapType = (state.autoScrolling || state.warping || wheeling) ? 'none' : 'x proximity';
    }
    // Always show the full figure (no $1.05M rounding) in either scale, and
    // shrink the font for very long numbers so $1T still fits on one line.
    if (!state.ended) {
      const txt = fmt(dollars);
      readout.textContent = onMile ? `${txt} 🎉` : txt;
      // Size off the number alone so the 🎉 doesn't trip the shrink threshold.
      readout.style.fontSize = txt.length > 12 ? '28px' : '38px';
    }

    hint.style.opacity = state.sl > 8 ? '0' : '1';

    const nx = nextMarker();
    // The next-benchmark slot shows in both scales: a green "Skip ahead"
    // auto-scroll button in the real scale, and a "Warp to next" button once
    // past $1M (mirroring the real-scale layout with warp semantics).
    const showNext = !state.autoScrolling && !state.benchActive && !state.warping
      && !state.ended && state.sl > NEXT_MIN && !!nx;
    nextBtn.style.display = showNext ? 'block' : 'none';
    if (nx) {
      if (inReal) {
        nextTitle.textContent = 'Skip ahead →';
        nextBtn.style.background = C.accent;
        nextBtn.style.boxShadow = '0 0 14px rgba(0,0,0,0.15)';
      } else {
        // Simulated scale: the slot becomes the black warp button.
        nextTitle.textContent = 'Warp to →';
        nextBtn.style.background = C.dark;
        nextBtn.style.boxShadow = '0 0 14px rgba(0,0,0,0.15)';
      }
      nextSub.textContent = nx.label;
    }
    // Pinned after the label is set, so the measured height accounts for a name
    // that wraps to two or more lines.
    pinAbove(nextBtn, btnBase, true);

    // "X more scrolling to <next>" hero — both scales, and it keeps ticking
    // down through a warp (toward the benchmark being warped to) rather than
    // disappearing. Space is reserved (see heroSub styles) so toggling never
    // nudges the timeline below it.
    // Point the hero at the nearest benchmark still AHEAD of the focus point, so
    // the countdown hits 0 exactly as that marker reaches centre, then flips to
    // the next one — keeping the readout and the marker in sequence. (The $11 Big
    // Mac / $60 gas need no special-casing: they start left of the focus, so
    // they're never "ahead" and the intro naturally reads "7 sec … to debt".)
    const heroNx = nx;
    const heroTarget = state.warping && state.warpTarget ? state.warpTarget : heroNx;
    const heroDollars = state.warpNum != null ? state.warpNum : (live ? rawDollars : 0);
    // When resting on a $1M/$1B milestone, show a "This would have taken X of
    // real scrolling." blurb (teal time) ABOVE the normal hero line, mirroring
    // the $1T end scene. The hero line ("X to scroll to <next>") stays below it.
    if (!state.ended && onMile) {
      mileSub.style.display = 'block';
      // Time to manually scroll from $1 all the way to this milestone ($1 = 1px
      // at the human SCROLL_RATE) — i.e. the running total, not the next hop.
      const took = humanizeWords(onMile / SCROLL_RATE);
      const mileName = (MARKERS.find(m => m.value === onMile) || {}).label || fmt(onMile);
      mileSub.innerHTML = `This would have taken ${tealVal(took)} of real scrolling to reach ${mileName}.`;
    } else {
      mileSub.style.display = 'none';
    }
    heroSub.style.display = state.ended ? 'none' : 'block';
    if (!state.ended && heroTarget) {
      const time = humanize(Math.max(0, (heroTarget.value - heroDollars) / SCROLL_RATE));
      const bench = noOrphan(sentenceCase(heroTarget.label), heroTarget.emoji);
      // New benchmark → reset the locked time width so it re-measures.
      if (heroTrack.key !== heroTarget.label) {
        heroTrack.key = heroTarget.label;
        heroTrack.w = 0;
        heroTime.style.minWidth = '';
      }
      heroTime.textContent = time;
      // Leading non-breaking space guarantees a gap after the locked-width time
      // and keeps the time and "to" from splitting across lines.
      heroTail.innerHTML = `&nbsp;to scroll to <strong>${bench}</strong>`;
      // Grow-and-lock the time width to its widest value this countdown, so the
      // ticking number never shifts the benchmark text between lines.
      const w = heroTime.offsetWidth;
      if (w > heroTrack.w) {
        heroTrack.w = w;
        heroTime.style.minWidth = `${w}px`;
      }
    } else {
      heroTime.textContent = '';
      heroTail.textContent = '';
      heroTime.style.minWidth = '';
      heroTrack.key = '';
      heroTrack.w = 0;
    }

    chipBtn.style.display = state.benchActive ? 'block' : 'none';
    if (state.benchActive) {
      chipNum.textContent = 'Scrolling to →';
      chipLabel.textContent = state.label;
      pinAbove(chipBtn, btnBase, true);
    }
  }

  // --------------------------------------------------------- scrolling ------
  function onScroll() {
    if (!scrollEl) return;
    const prevSL = track.sl;
    const now = performance.now();
    const sl = scrollEl.scrollLeft;
    state.sl = sl;
    if (!state.warping) state.ended = sl + FOCUS_PX >= xOf(MAX_VALUE);

    // Simulated scale: when a leg's finite forward runway is exhausted
    // (scrolling forward), auto-warp up to the next benchmark. Measured at the
    // focus point, where the readout is read.
    const rp = sl + FOCUS_PX;
    if (!state.autoScrolling && !state.ended && sl > prevSL && rp >= J1M_X
      && now >= track.cooldown) {
      const i = simLeg(rp);
      const forward = rp - markerX(i);
      if (i < LEGS && forward >= RUN_PX - RUNWAY_END) {
        track.sl = sl;
        track.t = now;
        warpToNext(i + 1);
        return;
      }
    }

    track.sl = sl;
    track.t = now;
    render();
  }

  // The signature "warp": rocket the readout up to benchmark JRN[i] while the
  // strip snaps to its marker. Used both by auto-warp (runway exhausted) and
  // the manual Warp-speed button.
  function warpToNext(i) {
    if (i < 0 || i >= JRN.length || !scrollEl) return;
    if (raf) cancelAnimationFrame(raf);
    const startPx = scrollEl.scrollLeft;
    // Land with the benchmark at the focus point, matching the real scale, so it
    // reads its exact value (e.g. $1,000,000,000) on arrival.
    const targetPx = Math.max(0, markerX(i) - FOCUS_PX);
    const startD = dollarsOf(startPx + FOCUS_PX);
    const targetD = dollarsOf(targetPx + FOCUS_PX);
    // The final warp to the top benchmark uses its own custom duration — but only
    // for the genuine full-gap leap (from the previous benchmark). A short warp
    // back up to $1T from within its own buffer stays quick.
    const finaleGap = JRN.length > 1 ? JRN[LEGS].value - JRN[LEGS - 1].value : Infinity;
    const isFinale = i >= LEGS && (targetD - startD) > finaleGap * 0.5;
    const dur = isFinale ? WARP_FINALE_MS : warpDur(targetD - startD);
    const t0 = performance.now();
    state.autoScrolling = true;
    state.warping = true;
    state.benchActive = false;
    // Keep the "… more scrolling to X" line alive so it ticks down as we arrive.
    state.warpTarget = { value: JRN[i].value, label: JRN[i].label, emoji: JRN[i].emoji };
    render();
    const step = (now) => {
      const t = Math.min(1, (now - t0) / dur);
      const e = easeInOut(t);
      scrollEl.scrollLeft = startPx + (targetPx - startPx) * e;
      state.sl = scrollEl.scrollLeft;
      state.warpNum = startD + (targetD - startD) * e;
      if (t < 1) {
        raf = requestAnimationFrame(step);
        render();
      } else {
        scrollEl.scrollLeft = targetPx;
        state.sl = targetPx;
        state.autoScrolling = false;
        state.warping = false;
        state.warpNum = null;
        state.warpTarget = null;
        state.ended = targetPx + FOCUS_PX >= xOf(MAX_VALUE);
        track.sl = targetPx;
        track.t = performance.now();
        track.cooldown = performance.now() + 400;
        render();
      }
    };
    raf = requestAnimationFrame(step);
  }

  function animateTo(targetPx, dur, onDone) {
    if (!scrollEl || state.autoScrolling) return;
    const start = scrollEl.scrollLeft;
    const t0 = performance.now();
    state.autoScrolling = true;
    render();
    const step = (now) => {
      const t = Math.min(1, (now - t0) / dur);
      scrollEl.scrollLeft = start + (targetPx - start) * easeInOut(t);
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        state.autoScrolling = false;
        render();
        if (onDone) onDone();
      }
    };
    raf = requestAnimationFrame(step);
  }

  // Real-scale "Next benchmark" auto-scroll. Uses the same warpDur() curve as
  // the simulated warps, so the time-to-next-benchmark rises on one continuous
  // progression across the whole journey instead of a separate dollar-paced feel.
  function autoScrollLinear(target, label) {
    if (!scrollEl || state.autoScrolling) return;
    const start = scrollEl.scrollLeft;
    const startD = dollarsOf(start + FOCUS_PX);
    const targetD = dollarsOf(target + FOCUS_PX);
    const dur = warpDur(targetD - startD);
    const t0 = performance.now();
    state.autoScrolling = true;
    state.benchActive = true;
    state.label = label;
    render();
    const step = (now) => {
      const t = Math.min(1, (now - t0) / dur);
      scrollEl.scrollLeft = start + (target - start) * t;
      state.sl = scrollEl.scrollLeft;
      if (t < 1) {
        raf = requestAnimationFrame(step);
        render();
      } else {
        state.autoScrolling = false;
        state.benchActive = false;
        render();
      }
    };
    raf = requestAnimationFrame(step);
  }

  // ----------------------------------------------------------- actions ------
  function goNext() {
    const m = nextMarker();
    if (!m) return;
    const target = Math.max(0, xOf(m.value) - FOCUS_PX);
    autoScrollLinear(target, m.label);
  }

  function warp() {
    if (dollarsOf(state.sl + FOCUS_PX) >= REAL_MAX) {
      // Simulated: warp to the next benchmark AHEAD of the focus. This handles
      // both the forward runway (next benchmark up) and scrolling back into a
      // benchmark's approach buffer (warps forward to that same benchmark, e.g.
      // back up to $1T after scrolling down into its buffer).
      const nx = nextMarker();
      if (!nx) return;
      const idx = JRN.findIndex(j => j.value === nx.value);
      if (idx >= 0) warpToNext(idx);
      return;
    }
    // Real scale: warp to the $1M seam.
    if (raf) cancelAnimationFrame(raf);
    state.autoScrolling = false;
    state.benchActive = false;
    const target = xOf(REAL_MAX) - FOCUS_PX;
    animateTo(target, warpDur(REAL_MAX - dollarsOf(state.sl + FOCUS_PX)));
  }

  function cancelAuto() {
    if (raf) cancelAnimationFrame(raf);
    state.autoScrolling = false;
    state.benchActive = false;
    render();
  }

  function restartAll() {
    if (raf) cancelAnimationFrame(raf);
    state.autoScrolling = false;
    state.benchActive = false;
    state.ended = false;
    state.sl = 0;
    if (scrollEl) scrollEl.scrollLeft = 0;
    render();
  }

  // Scale every stem so the deepest marker (dot + stem + emoji + value + label)
  // still fits under the timeline within the stage, whatever height the dek /
  // header ends up being. Ratios between stems are preserved so Big Mac and gas
  // stay staggered and the accent markers stay a touch longer.
  const IDEAL_STEM = 128; // the longest (accent) base stem
  const STACK_BELOW = 96; // emoji + value + label + margins hanging below a stem
  function fitStems() {
    const stageH = stage.clientHeight;
    if (!stageH) return;
    const maxStem = stageH - RULE_Y - STACK_BELOW;
    const scale = Math.max(0, Math.min(1, maxStem / IDEAL_STEM));
    stems.forEach(({ el: s, base }) => { s.style.height = `${Math.round(base * scale)}px`; });
  }

  // ------------------------------------------------------------- boot -------
  scrollEl = buildStrip();
  stage.appendChild(scrollEl);
  stage.appendChild(hint);
  stage.appendChild(nextBtn);
  stage.appendChild(chipBtn);
  bindWheel(scrollEl);
  fitStems();
  requestAnimationFrame(fitStems);

  // Dev shortcut to the $1M seam. Gated on localhost / ?dev so it can live in the
  // source permanently instead of being added and torn out each time it's needed.
  // A bare IP covers previewing on a phone over the LAN as well as 127.0.0.1;
  // production is always served from a real domain, so it can't match.
  const host = window.location.hostname;
  const isDev = host === 'localhost' || host === '[::1]' || host.endsWith('.local')
    || /^\d+\.\d+\.\d+\.\d+$/.test(host)
    || /[?&]dev(&|=|$)/.test(window.location.search);
  if (isDev) {
    stage.appendChild(el('button', {
      position: 'absolute',
      bottom: '8px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '30',
      background: '#fff',
      color: C.muted,
      border: `1px dashed ${C.rule}`,
      borderRadius: '6px',
      padding: '4px 10px',
      fontFamily: 'inherit',
      fontSize: '11px',
      fontWeight: '600',
      cursor: 'pointer',
    }, {
      text: 'dev: skip to $1M',
      onClick: () => {
        if (!scrollEl) return;
        // Drop out of any running warp/auto-scroll first, or its rAF would keep
        // animating from the old position and drag us straight back.
        if (raf) cancelAnimationFrame(raf);
        state.autoScrolling = false;
        state.benchActive = false;
        state.warping = false;
        state.warpNum = null;
        state.warpTarget = null;
        state.ended = false;
        // Land $1M on the focus point, exactly where a warp would leave it.
        const target = Math.max(0, xOf(REAL_MAX) - FOCUS_PX);
        state.sl = target;
        track.sl = target; // so onScroll doesn't read the jump as a fast forward scroll
        scrollEl.scrollLeft = target;
        render();
      },
    }));
  }

  track.sl = 0;
  track.t = performance.now();
  requestAnimationFrame(() => { if (scrollEl) scrollEl.scrollLeft = 0; });
  render();

  if (window.pymChild) window.pymChild.sendHeight();
}
